<template>
    <MainComponent :id="anchorsPageProfile.appraisalsSection">
        <template #title>
            <!-- eslint-disable vue/no-v-html -->
            <h2 v-if="isEditMode" v-html="editModeTitle"></h2>
            <h2 v-else-if="appraisals && appraisals.missionCount > 0" v-html="classicTitle"></h2>
            <!-- eslint-enable vue/no-v-html -->
        </template>
        <template #title-action>
            <TranslateButton v-if="isTranslationActivated && hasAppraisals" section="appraisal" />
        </template>
        <template v-if="appraisals" #content>
            <AppraisalGeneralRatings :rating="appraisals.ratings.general || 0" :appraisal-counter="appraisals.numberOfAppraisalsWithRating || 0" />
            <AppraisalRatings v-if="isEditMode && !hasAppraisals" :model-value="sampleRatings()" :is-sample="true" />
            <AppraisalRatings v-if="hasAppraisals" :model-value="appraisals.ratings" :is-sample="false" />
            <AppraisalComponent v-if="isEditMode && !hasAppraisals" :model-value="sampleAppraisal()" :is-sample="true" :is-edit-mode="isEditMode" />
            <ShowMoreByMaxHeight
                v-if="hasAppraisalsWithComment"
                :label="t('profile.section.appraisals.show.all.label.v2', {count: appraisalsWithComment.length})"
                :locked="{
                    allowHref: true,
                    label: t('profile.section.appraisals.private', [profile?.firstname]),
                    componentName: 'profile-appraisals-section',
                    targetId: anchorsPageProfile.appraisalsSection,
                }"
                :max-height="150"
            >
                <AppraisalComponent
                    v-for="(appraisal, index) in appraisalsWithComment"
                    :key="`appraisal-with-comment-${index}`"
                    :model-value="appraisal"
                    :is-sample="false"
                    :is-edit-mode="isEditMode"
                />
            </ShowMoreByMaxHeight>
            <HiddenAppraisalIndicatorOld
                v-if="profile && !isEditMode && hasHiddenAppraisals"
                :account-firstname="profile.firstname"
                :hidden-counter="appraisals.hiddenAppraisals"
            />
            <PushPanel v-if="isEditMode && !hasAppraisals" />
        </template>
    </MainComponent>
</template>

<script setup lang="ts">
    import {storeToRefs} from 'pinia';
    import {computed, onMounted, onUnmounted, useTranslation} from '#imports';
    import ShowMoreByMaxHeight from '~/components/_common/show-more/ShowMoreByMaxHeight.vue';
    import AppraisalComponent from '~/components/main/appraisals/Appraisal.vue';
    import AppraisalGeneralRatings from '~/components/main/appraisals/AppraisalGeneralRatings.vue';
    import AppraisalRatings from '~/components/main/appraisals/AppraisalRatings.vue';
    import HiddenAppraisalIndicatorOld from '~/components/main/appraisals/HiddenAppraisalIndicatorOld.vue';
    import PushPanel from '~/components/main/appraisals/PushPanel.vue';
    import MainComponent from '~/components/main/common/MainComponent.vue';
    import TranslateButton from '~/components/main/TranslateButton.vue';
    import {anchorsPageProfile} from '~/config/anchors';
    import {eventBus} from '~/config/events';
    import {useProfileStore} from '~/store/profileStore';
    import {TranslationDirection, useTranslationStore} from '~/store/translation/translationStore';
    import type {Appraisal, AppraisalAverageRating} from '~/types/domain/Appraisals';

    // TODO: to remove with ff profile-revamp
    const {t} = useTranslation();
    const {appraisals, profile} = storeToRefs(useProfileStore());

    const {isTranslationActivated} = storeToRefs(useTranslationStore());
    const {translateAll, replaceTranslationContent} = useTranslationStore();
    const translationSection = 'appraisals';
    const translationField = 'comment';

    const appraisalsWithComment = computed(() =>
        hasAppraisals.value ? appraisals.value!.appraisals.filter((appraisal: Appraisal) => appraisal.comment) : [],
    );
    const hasAppraisals = computed<boolean>(() => (appraisals && appraisals.value!.appraisals.length > 0) || false);
    const hasAppraisalsWithComment = computed<boolean>(() => appraisalsWithComment.value.length > 0);
    const hasHiddenAppraisals = computed<boolean>(() => (appraisals && appraisals.value!.hiddenAppraisals > 0) || false);
    const isEditMode = computed<boolean>(() => profile.value?.editMode || false);

    function sampleRatings(): AppraisalAverageRating {
        return {
            communication: 4.5,
            quality: 5,
            delay: 4,
            general: 4.5,
        } as AppraisalAverageRating;
    }

    function sampleAppraisal(): Appraisal {
        return {
            clientName: 'Valerie',
            clientCompanyName: 'Miller Inc.',
            comment:
                'Vestibulum malesuada auctor lectus a convallis. Orci varius natoque penatibus et magnis dis parturient montes, nascetur ridiculus\nmus.',
            hidden: true,
            creationDate: '2022-09-16',
            clientImageUrl: '',
        } as Appraisal;
    }

    const editModeTitle = computed(() => {
        if (hasAppraisals.value) {
            return t('profile.section.mission.title', {nbOfMissions: appraisals.value!.missionCount});
        }

        return t('profile.mission.block.title.placeholder');
    });

    const classicTitle = computed(() => t('profile.section.mission.title', {nbOfMissions: appraisals.value!.missionCount}));

    async function translateAppraisals() {
        const appraisalCommentRequests = appraisalsWithComment.value.map((appraisal: Appraisal, index: number) => {
            return {
                section: translationSection,
                field: translationField,
                index,
                originalContent: appraisal.comment ?? ' ',
            };
        });

        await translateAll(appraisalCommentRequests);
        replaceTranslationContent(translationSection, translationField, TranslationDirection.TRANSLATE, switchTranslation);
    }

    function restoreAppraisals() {
        replaceTranslationContent(translationSection, translationField, TranslationDirection.RESTORE, switchTranslation);
    }

    function switchTranslation(content: string, index: number) {
        appraisalsWithComment.value[index].comment = content;
    }

    onMounted(() => {
        eventBus.on('translation:request', translateAppraisals);
        eventBus.on('translation:restore', restoreAppraisals);
    });

    onUnmounted(() => {
        eventBus.off('translation:request', translateAppraisals);
        eventBus.off('translation:restore', restoreAppraisals);
    });
</script>
